"use client";
import { FocusTrap } from "@aleksundshantu/aunds-utils-react/accessibility";
import { removeExtraSpacesFromClassNames } from "@/utils/removeExtraSpacesFromClassNames";
import { useModal } from "../ModalRoot/ModalRoot";
import styles from "./ModalContent.module.scss";

/** A dialog needs an accessible name — either a label or the id of its heading */
export type ModalAccessibleName =
  | { ariaLabel: string; ariaLabelledby?: never }
  | { ariaLabelledby: string; ariaLabel?: never };

export type ModalContentProps = ModalAccessibleName & {
  children: React.ReactNode;
  ariaDescribedby?: string;
  className?: string;
};

const ModalContent = ({
  children,
  ariaLabel,
  ariaLabelledby,
  ariaDescribedby,
  className = "",
}: ModalContentProps) => {
  const { mounted, setOpen, absolutePosition, state, animation } = useModal();

  return (
    mounted && (
      // eslint-disable-next-line jsx-a11y/no-autofocus -- ARIA APG: focus must move into dialog on open
      <FocusTrap autoFocus>
        {/* Escape is handled in ModalRoot; click on the backdrop area closes the modal */}
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
        <div
          role="dialog"
          aria-modal="true"
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledby}
          aria-describedby={ariaDescribedby}
          className={removeExtraSpacesFromClassNames(
            `${styles["modal-content"]} ${className}`,
          )}
          data-absolute-position={absolutePosition}
          data-state={state}
          data-animation={animation}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setOpen(false);
            }
          }}
        >
          {children}
        </div>
      </FocusTrap>
    )
  );
};

export default ModalContent;
